// Fond de plan : page d'un PDF rendue dans un canvas (pdfjs), ou image. Seuls PDF et images sont lus (pas de DWG ni d'IFC).
// Le repère du plan = page PDF à l'échelle 1 (points PDF) ou pixels naturels de l'image : les tracés y sont stockés.
import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

let pdfjsCharge: Promise<typeof import('pdfjs-dist')> | null = null;
function chargerPdfjs() {
  pdfjsCharge ??= import('pdfjs-dist').then((lib) => { lib.GlobalWorkerOptions.workerSrc = workerUrl; return lib; });
  return pdfjsCharge;
}

const PIXELS_MAX = 16_000_000;

interface Props {
  url: string; mime: string; page: number; zoom: number;
  surTaille: (t: { l: number; h: number }) => void;
  surErreur: (message: string) => void;
}

export function FondPlan({ url, mime, page, zoom, surTaille, surErreur }: Props) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [doc, setDoc] = useState<{ pdf: PDFDocumentProxy; l: number; h: number } | null>(null);
  const [taille, setTaille] = useState<{ l: number; h: number } | null>(null);
  const tache = useRef<RenderTask | null>(null);
  const estPdf = mime === 'application/pdf';

  // Ouverture du PDF et dimensions de la page au repère du plan (échelle 1).
  useEffect(() => {
    if (!estPdf) return;
    let annule = false;
    let chargement: { destroy(): Promise<void> } | null = null;
    (async () => {
      try {
        const lib = await chargerPdfjs();
        const t = lib.getDocument({ url, withCredentials: true });
        chargement = t;
        const ouvert = await t.promise;
        if (annule) return;
        if (page > ouvert.numPages) { surErreur(`Le document ne compte que ${ouvert.numPages} page(s).`); return; }
        const p = await ouvert.getPage(page);
        const v = p.getViewport({ scale: 1 });
        if (annule) return;
        setDoc({ pdf: ouvert, l: v.width, h: v.height });
        setTaille({ l: v.width, h: v.height });
        surTaille({ l: v.width, h: v.height });
      } catch {
        if (!annule) surErreur('Impossible d’afficher ce PDF (fichier protégé, corrompu ou indisponible).');
      }
    })();
    return () => { annule = true; tache.current?.cancel(); chargement?.destroy().catch(() => {}); };
  }, [url, page, estPdf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rendu à la résolution du zoom (différé pour ne pas recalculer à chaque cran).
  useEffect(() => {
    if (!doc || !canvas.current) return;
    const t = window.setTimeout(async () => {
      try {
        const p = await doc.pdf.getPage(page);
        const dpr = window.devicePixelRatio || 1;
        const s = Math.min(zoom * dpr, Math.sqrt(PIXELS_MAX / (doc.l * doc.h)));
        const v = p.getViewport({ scale: s });
        const c = canvas.current!;
        tache.current?.cancel();
        c.width = Math.floor(v.width); c.height = Math.floor(v.height);
        tache.current = p.render({ canvas: c, viewport: v });
        await tache.current.promise;
      } catch (e: any) {
        if (e?.name !== 'RenderingCancelledException') surErreur('Le rendu de la page du plan a échoué.');
      }
    }, 120);
    return () => window.clearTimeout(t);
  }, [doc, zoom, page]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!estPdf) {
    return (
      <img src={url} alt="" className="cch-fond" draggable={false}
        style={taille ? { width: taille.l * zoom, height: taille.h * zoom } : undefined}
        onLoad={(e) => { const t = { l: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight }; setTaille(t); surTaille(t); }}
        onError={() => surErreur('Impossible d’afficher cette image.')} />
    );
  }
  return <canvas ref={canvas} className="cch-fond" aria-hidden style={doc ? { width: doc.l * zoom, height: doc.h * zoom } : undefined} />;
}
